import React, { useEffect, useRef } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { Picker, Row, Span } from 'components'
import { Views } from 'components/DatePicker/DatePicker'
import moment, { Moment } from 'moment'
import { useForm } from 'react-hook-form'
import { SPACING, OLDYUP as YUP } from 'utils'
import * as Yup from 'yup'

interface IDateRange {
  label: [string, string]
  formValue?: [string, string]
  control?: any
  errors?: any
  watch?: any
  width?: [string, string] | string
  minDate?: [string | Moment, string | Moment]
  maxDate?: [string | Moment, string | Moment]
  defaultValue?: Array<Date | null>
  textBetween?: string
  marginRight?: [string, string] | string
  marginBottom?: [string, string] | string
  placeholder?: [string, string]
  inputFormat?: string
  views?: Views[]
  type?: 'day' | 'monthAndYear' | 'dayMonthYear' | 'default'
  disabled?: [boolean, boolean] | boolean
  onChangeDate?: [(_Date: Date | null) => void, (_Date: Date | null) => void]
  value?: [Date | null, Date | null]
  alignItems?: 'flex-end' | 'flex-start' | 'space-between' | 'center'
  flex?: string
  onValidDate?: [(_date) => void, (_date) => void]
}

const DateRange: React.FC<IDateRange> = ({
  label,
  formValue,
  control,
  errors,
  watch,
  width = [],
  minDate = [],
  maxDate = [],
  defaultValue = [],
  marginRight = [],
  marginBottom = [],
  textBetween,
  placeholder = ['dd/mm/aaaa', 'dd/mm/aaaa'],
  type = 'default',
  disabled = [false, false],
  onChangeDate = [],
  value = [null, null],
  inputFormat,
  views,
  alignItems = 'flex-start',
  flex,
  onValidDate = null
}) => {
  const schema = Yup.object().concat(
    YUP.dateRange('startDate', 'endDate').required
  )

  const {
    watch: stateWatch,
    control: stateControl,
    setValue: stateSetValue,
    formState: { errors: stateErrors }
  } = useForm<{ startDate: Date; endDate: Date }>({
    resolver: yupResolver(schema)
  })

  const startDate = formValue ? watch(formValue[0]) : stateWatch('startDate')
  const endDate = formValue ? watch(formValue[1]) : stateWatch('endDate')

  const isMounted = useRef(false)

  // Seta o state (value[0 e 1]) quando o valor do formValue muda:
  // startDate (useForm) -> value[0] (estado)
  // endDate (useForm)   -> value[1] (estado)
  useEffect(() => {
    if (onValidDate) {
      return
    }

    // Não passou as props onChangeDate, ta usando control,
    // watch, errors e formValue (useForm).
    if (onChangeDate.length === 0) return

    // Impede de rodar o onChange no render do componente, se não é
    // feito uma atualização de estado desnecessária na tela que ta usando.
    if (!isMounted.current) {
      isMounted.current = true
      return
    }

    // Quando a data inicial muda no picker (controlada pelo useForm, "startDate"),
    // roda um onChangeDate[0] pra mudar o state da data inicial fora do componente.
    if (value[0] !== startDate) {
      onChangeDate[0](startDate)
    }

    // Quando a data final muda no picker (controlada pelo useForm, "endDate"),
    // roda um onChangeDate[1] pra mudar o state da data final fora do componente.
    if (value[1] !== endDate) {
      onChangeDate[1](endDate)
    }
  }, [startDate, endDate])

  // Seta o valor padrão do estado no valor inicial do formValue.
  // Também acompanha o valor do estado e seta no formValue:
  // value[0] (estado) -> startDate (useForm)
  // value[1] (estado) -> endDate (useForm)
  useEffect(() => {
    if (onValidDate) {
      return
    }

    if (value[0] !== undefined && value[0] !== startDate) {
      stateSetValue('startDate', value[0])
    }

    if (value[1] !== undefined && value[1] !== endDate) {
      stateSetValue('endDate', value[1])
    }
  }, [value[0], value[1]])

  const getProp = (prop: string | Array<string>, i: 0 | 1): string => {
    return typeof prop === 'string' ? prop : prop?.[i] || null
  }

  return (
    <Row alignItems={alignItems} flexWrap="nowrap">
      <Picker
        control={control ? control : onValidDate ? null : stateControl}
        errors={control ? errors : stateErrors}
        formValue={control ? formValue[0] : 'startDate'}
        value={onValidDate ? value[0] : null}
        label={label[0]}
        marginRight={getProp(marginRight, 0) || SPACING.spacing10}
        marginBottom={getProp(marginBottom, 0)}
        width={getProp(width, 0)}
        maxDate={maxDate[0] || endDate}
        onChange={onChangeDate[0]}
        minDate={type === 'day' ? moment().startOf('month') : undefined}
        defaultValue={defaultValue[0] || null}
        placeholder={placeholder[0]}
        views={views}
        inputFormat={inputFormat}
        type={type}
        disabled={typeof disabled == 'boolean' ? disabled : disabled[0]}
        flex={flex}
      />
      {textBetween && (
        <Span
          text={textBetween}
          marginRight={SPACING.spacing10}
          marginTop={SPACING.spacing30}
          style={{ alignSelf: 'flex-start', wordBreak: 'normal' }}
        />
      )}
      <Picker
        control={control ? control : onValidDate ? null : stateControl}
        errors={control ? errors : stateErrors}
        label={label[1]}
        marginLeft={textBetween ? null : SPACING.spacing5}
        marginRight={getProp(marginRight, 1)}
        marginBottom={getProp(marginBottom, 1)}
        formValue={control ? formValue[1] : 'endDate'}
        value={onValidDate ? value[1] : null}
        width={getProp(width, 1)}
        minDate={minDate[1] || startDate}
        maxDate={type === 'day' ? moment().endOf('month') : undefined}
        defaultValue={defaultValue[1] || null}
        views={views}
        onChange={onChangeDate[1]}
        inputFormat={inputFormat}
        placeholder={placeholder[1]}
        type={type}
        disabled={typeof disabled == 'boolean' ? disabled : disabled[1]}
        flex={flex}
      />
    </Row>
  )
}

export default DateRange
